import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { AddBlacklistModal } from '../creatorManager/AddBlacklistModal';
import { QuickOutreachModal } from '../components/QuickOutreachModal';
import { CreateTargetInviteModal } from '../invitation/CreateTargetInviteModal';
import { CategorySelector, categoryLabel } from '../../../components/CategorySelector';
import { StatusMark } from '../../../components/StatusMark';
import { TagFilterPanel } from '../../../components/TagFilterPanel';
import { useTranslation } from '../../../i18n';
import { useSelectedShop } from '../layout/selectedShopContext';
import { addInfluencerCollection, cancelInfluencerCollection, getCreatorProductCategories, getInfluencerPage } from '../../../store/request';
import type { CreatorProductCategory } from '../../../types/category';
import type { InfluencerRecord } from '../../../types/influencer';

const searchBtnBg = `${process.env.PUBLIC_URL || ''}/search_bg.png`;

const regionLabelKey: Record<string, string> = {
    VN: 'talent.market_vn',
    TH: 'talent.market_th',
    ID: 'talent.market_id',
    MY: 'talent.market_my',
    PH: 'talent.market_ph',
    SG: 'talent.market_sg',
};

function formatCount(value?: number) {
    if (value == null || !Number.isFinite(value)) return '—';
    return value.toLocaleString('en-US');
}

function formatGmv(amount?: number, currency?: string) {
    if (amount == null || !Number.isFinite(amount)) return '—';
    const text = amount.toLocaleString('en-US', { maximumFractionDigits: 2 });
    return currency ? `${currency} ${text}` : text;
}

function statusKeyOf(status?: number) {
    if (status === 1) return 'replied';
    if (status === 2) return 'cooperated';
    if (status === 3) return 'blacklist';
    return 'pending';
}

function rowKeyOf(row: InfluencerRecord) {
    if (row.influencerId?.trim()) return row.influencerId.trim();
    if (row.id != null) return String(row.id);
    return row.creatorOpenId || row.username || '';
}

function influencerIdOf(row: InfluencerRecord) {
    return row.influencerId?.trim() || '';
}

function isBlacklistedTalent(row: InfluencerRecord) {
    return Boolean(row.isBlacklist) || row.status === 3;
}

function statusTag(statusKey: string, label: string) {
    if (statusKey === 'replied' || statusKey === 'published' || statusKey === 'cooperated') {
        return <StatusMark tone="success">{label}</StatusMark>;
    }
    if (statusKey === 'pending') {
        return <StatusMark tone="pending">{label}</StatusMark>;
    }
    if (statusKey === 'blacklist') {
        return <StatusMark tone="danger">{label}</StatusMark>;
    }
    return <StatusMark tone="review">{label}</StatusMark>;
}

export const TalentSquare = observer(() => {
    const { t } = useTranslation();
    const { shopId } = useSelectedShop();
    const navigate = useNavigate();
    const [keyword, setKeyword] = useState('');
    const [appliedKeyword, setAppliedKeyword] = useState('');
    const [market, setMarket] = useState('vn');
    const [categories, setCategories] = useState<CreatorProductCategory[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<CreatorProductCategory | null>(null);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [records, setRecords] = useState<InfluencerRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [collectingId, setCollectingId] = useState<string | null>(null);
    const [batchCollecting, setBatchCollecting] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [outreachOpen, setOutreachOpen] = useState(false);
    const [outreachPresets, setOutreachPresets] = useState<InfluencerRecord[]>([]);

    const [createInviteOpen, setCreateInviteOpen] = useState(false);
    const [createInviteTalents, setCreateInviteTalents] = useState<InfluencerRecord[]>([]);
    const [blacklistTarget, setBlacklistTarget] = useState<{ influencerId: string; talentName: string } | null>(
        null
    );

    const openBlacklist = (row: InfluencerRecord) => {
        const influencerId = influencerIdOf(row);
        if (!influencerId) {
            message.warning(t('creator.blacklist_missing_id'));
            return;
        }
        setBlacklistTarget({
            influencerId,
            talentName: row.nickname || row.username || '—',
        });
    };

    const openOutreach = (rows: InfluencerRecord[]) => {
        setOutreachPresets(rows.filter((row) => row.creatorOpenId?.trim()));
        setOutreachOpen(true);
    };

    const openCreateInvite = (rows: InfluencerRecord[]) => {
        if (!shopId) {
            message.warning(t('invitation.invite_shop_required'));
            return;
        }
        const talents = rows.filter((row) => row.creatorOpenId?.trim() && !isBlacklistedTalent(row));
        if (talents.length === 0) {
            message.warning(t('talent.missing_open_id'));
            return;
        }
        setCreateInviteTalents(talents);
        setCreateInviteOpen(true);
    };

    const confirmUnfavorite = (row: InfluencerRecord) => {
        const influencerId = influencerIdOf(row);
        const talentName = row.nickname || row.username || '—';
        if (!influencerId) {
            message.warning(t('talent.missing_open_id'));
            return;
        }
        Modal.confirm({
            title: t('creator.unfavorite'),
            content: t('creator.unfavorite_confirm', { name: talentName }),
            okText: t('common.confirm'),
            cancelText: t('common.cancel'),
            centered: true,
            onOk: async () => {
                const res = await cancelInfluencerCollection(influencerId);
                if (res.code !== 200) {
                    message.error(res.message || t('creator.unfavorite_failed'));
                    return Promise.reject(new Error(res.message || 'cancel failed'));
                }
                message.success(res.message || t('creator.unfavorite_success', { name: talentName }));
                setRecords((prev) =>
                    prev.map((item) =>
                        influencerIdOf(item) === influencerId ? { ...item, isCollect: false } : item
                    )
                );
            },
        });
    };

    const collectTalent = async (row: InfluencerRecord) => {
        const influencerId = influencerIdOf(row);
        if (!influencerId) {
            message.warning(t('talent.missing_open_id'));
            return;
        }
        if (row.isCollect || collectingId) return;
        setCollectingId(influencerId);
        try {
            const res = await addInfluencerCollection({ influencerIds: [influencerId] });
            if (res.code !== 200) {
                message.error(res.message || t('talent.collect_failed'));
                return;
            }
            message.success(res.message || t('talent.collect_success'));
            setRecords((prev) =>
                prev.map((item) =>
                    influencerIdOf(item) === influencerId ? { ...item, isCollect: true } : item
                )
            );
        } catch {
            message.error(t('talent.collect_failed'));
        } finally {
            setCollectingId(null);
        }
    };

    const collectSelected = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('talent.select_required'));
            return;
        }
        const selectedKeySet = new Set(selectedRowKeys.map(String));
        const pending = records.filter((row) => selectedKeySet.has(rowKeyOf(row)) && !row.isCollect);
        const influencerIds = pending
            .map((row) => influencerIdOf(row))
            .filter((id): id is string => Boolean(id));
        if (pending.length === 0) {
            message.warning(t('talent.already_collected'));
            return;
        }
        if (influencerIds.length === 0) {
            message.warning(t('talent.missing_open_id'));
            return;
        }
        setBatchCollecting(true);
        try {
            const res = await addInfluencerCollection({ influencerIds });
            if (res.code !== 200) {
                message.error(res.message || t('talent.collect_failed'));
                return;
            }
            message.success(res.message || t('talent.collect_success'));
            const collected = new Set(influencerIds);
            setRecords((prev) =>
                prev.map((item) =>
                    collected.has(influencerIdOf(item)) ? { ...item, isCollect: true } : item
                )
            );
        } catch {
            message.error(t('talent.collect_failed'));
        } finally {
            setBatchCollecting(false);
        }
    };

    const marketOptions = useMemo(
        () => [{ value: 'vn', label: t('talent.market_vn') }],
        [t],
    );

    const selectedCategoryId = selectedCategory?.categoryId?.trim() ?? '';

    useEffect(() => {
        let cancelled = false;
        getCreatorProductCategories()
            .then((res) => {
                if (cancelled) return;
                if (res.code !== 200 || !Array.isArray(res.content)) {
                    if (res.code !== -1) message.error(res.message || t('talent.category_load_failed'));
                    setCategories([]);
                    return;
                }
                setCategories(res.content);
            })
            .catch(() => {
                if (!cancelled) message.error(t('talent.category_load_failed'));
            });
        return () => {
            cancelled = true;
        };
    }, [t]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const text = appliedKeyword.trim();
        getInfluencerPage({
            pageNum,
            pageSize,
            ...(text ? { keyword: text } : {}),
            selectionRegion: market.toUpperCase(),
            ...(selectedCategoryId ? { categoryIds: [selectedCategoryId] } : {}),
        })
            .then((res) => {
                if (cancelled) return;
                if (res.code !== 200) {
                    message.error(res.message || t('talent.load_failed'));
                    setRecords([]);
                    setTotal(0);
                    return;
                }
                const page = res.content;
                const list = page?.records;
                setRecords(Array.isArray(list) ? list : []);
                setTotal(page?.total ?? 0);
            })
            .catch(() => {
                if (!cancelled) message.error(t('talent.load_failed'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [appliedKeyword, market, pageNum, pageSize, selectedCategoryId, t]);

    useEffect(() => {
        const blocked = new Set(records.filter(isBlacklistedTalent).map((row) => rowKeyOf(row)));
        if (blocked.size === 0) return;
        setSelectedRowKeys((prev) => {
            const next = prev.filter((key) => !blocked.has(String(key)));
            return next.length === prev.length ? prev : next;
        });
    }, [records]);

    const columns = useMemo<ColumnsType<InfluencerRecord>>(
        () => [
            {
                title: t('talent.col_talent'),
                dataIndex: 'name',
                render: (_, row) => {
                    const name = row.nickname || row.username || '—';
                    const region = row.selectionRegion;
                    const regionKey = region ? regionLabelKey[region] : undefined;
                    const regionText = region ? (regionKey ? `${t(regionKey)} ${region}` : region) : '';
                    const categories = row.categoryNames?.filter(Boolean) ?? [];
                    return (
                        <div className="flex items-start gap-3 py-1">
                            {row.avatarUrl ? (
                                <img
                                    src={row.avatarUrl}
                                    alt=""
                                    className="size-10 shrink-0 rounded-full object-cover"
                                />
                            ) : (
                                <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-[#596ae8] text-sm font-semibold text-white">
                                    {(name === '—' ? '?' : name).slice(0, 1).toUpperCase()}
                                </span>
                            )}
                            <div className="min-w-0">
                                <div className="font-semibold text-slate-800">
                                    {name}{' '}
                                    {row.username ? (
                                        <span className="text-xs font-normal text-slate-400">@{row.username}</span>
                                    ) : null}
                                </div>
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                    {regionText ? (
                                        <Tag className="m-0 border-0 bg-slate-100 text-xs text-slate-600">
                                            {regionText}
                                        </Tag>
                                    ) : null}
                                    {categories.map((item) => (
                                        <Tag key={item} className="m-0 border-0 bg-slate-100 text-xs text-slate-600">
                                            {item}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                },
            },
            {
                title: t('talent.followers'),
                dataIndex: 'followerCount',
                render: (value?: number) => formatCount(value),
            },
            {
                title: t('talent.gmv30'),
                dataIndex: 'gmvAmount',
                render: (_: number | undefined, row) => formatGmv(row.gmvAmount, row.gmvCurrency),
            },
            {
                title: t('talent.status'),
                dataIndex: 'status',
                render: (status?: number) => {
                    const key = statusKeyOf(status);
                    const label =
                        key === 'replied'
                            ? t('talent.status_replied')
                            : key === 'cooperated'
                                ? t('talent.status_cooperated')
                                : key === 'blacklist'
                                    ? t('talent.status_blacklist')
                                    : t('talent.status_pending');
                    return statusTag(key, label);
                },
            },
            {
                title: t('talent.col_actions'),
                key: 'actions',
                render: (_, row) => (
                    <div className="flex flex-wrap items-center gap-0.5">
                        <Button
                            type="link"
                            className="wb-text-link"
                            href={
                                influencerIdOf(row)
                                    ? `/creator/talent_square/${encodeURIComponent(influencerIdOf(row))}`
                                    : undefined
                            }
                            disabled={!influencerIdOf(row)}
                            target={influencerIdOf(row) ? '_blank' : undefined}
                        >
                            {t('common.detail')}
                        </Button>

                        {row.isBlacklist || row.status === 3 ? (
                            <Button type="link" danger className="wb-text-link" disabled>
                                {t('talent.blacklist')}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    type="link"
                                    className={
                                        row.isCollect ? 'wb-text-link wb-text-link--active' : 'wb-text-link'
                                    }
                                    disabled={!row.isCollect && collectingId === influencerIdOf(row)}
                                    onClick={() => (row.isCollect ? confirmUnfavorite(row) : collectTalent(row))}
                                >
                                    {row.isCollect ? t('creator.unfavorite') : t('talent.favorite')}
                                </Button>
                                <Button
                                    type="link"
                                    className="wb-text-link"
                                    onClick={() => openOutreach(row.creatorOpenId ? [row] : [])}
                                >
                                    {t('talent.outreach')}
                                </Button>
                                <Button type="link" danger className="wb-text-link" onClick={() => openBlacklist(row)}>
                                    {t('talent.block')}
                                </Button>
                            </>
                        )}
                    </div>
                ),
            },
        ],
        [t, openOutreach, collectTalent, confirmUnfavorite, openBlacklist, collectingId],
    );

    return (
        <div className="container">
            <header className="relative mb-5 flex flex-col gap-3 md:min-h-10 md:flex-row md:items-center">
                <h1 className="relative z-[1] mb-0 mt-0 shrink-0 text-[22px] font-semibold leading-snug tracking-tight text-slate-800">
                    {t('talent.title')}
                </h1>
                <div className="flex w-full justify-center md:pointer-events-none md:absolute md:inset-0 md:items-center md:px-28">
                    <form
                        className="pointer-events-auto flex h-10 w-full max-w-[520px] items-stretch overflow-hidden rounded-full border-2 border-[#3b4dd7] bg-white shadow-[0_1px_2px_rgba(59,77,215,0.08)] md:mx-auto"
                        onSubmit={(e) => {
                            e.preventDefault();
                            setPageNum(1);
                            setAppliedKeyword(keyword.trim());
                        }}
                    >
                        <input
                            type="search"
                            className="min-w-0 flex-1 border-0 bg-transparent px-4 text-sm text-slate-800 outline-none placeholder:text-slate-400"
                            placeholder={t('talent.search_placeholder')}
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                        />
                        <button
                            type="submit"
                            className="flex w-12 shrink-0 cursor-pointer items-center justify-center border-0 bg-cover bg-center bg-no-repeat text-base text-white"
                            style={{ backgroundImage: `url(${searchBtnBg})` }}
                            aria-label={t('common.search')}
                        >
                            <SearchOutlined className="text-[18px] font-bold text-white drop-shadow-[0_0_0.35px_#fff]" />
                        </button>
                    </form>
                </div>
            </header>

            <TagFilterPanel
                className="mb-4"
                extraChips={
                    selectedCategory
                        ? [
                              {
                                  key: 'category',
                                  prefix: t('talent.category'),
                                  label: categoryLabel(selectedCategory),
                                  onRemove: () => {
                                      setSelectedCategory(null);
                                      setPageNum(1);
                                  },
                              },
                          ]
                        : []
                }
                onClear={() => {
                    setSelectedCategory(null);
                    setPageNum(1);
                }}
                rows={[
                    {
                        key: 'market',
                        label: t('talent.market'),
                        chipPrefix: t('talent.market'),
                        value: market,
                        options: marketOptions,
                        clearable: false,
                        onChange: (value) => {
                            setMarket(value);
                            setPageNum(1);
                        },
                    },
                ]}
            >
                <div className="flex gap-4 border-t border-[#eef0f4] py-3.5">
                    <span className="w-[72px] shrink-0 pt-0.5 text-sm text-slate-500">{t('talent.category')}</span>
                    <CategorySelector
                        categories={categories}
                        value={selectedCategoryId}
                        onChange={(category) => {
                            setSelectedCategory(category);
                            setPageNum(1);
                        }}
                    />
                </div>
            </TagFilterPanel>

            <section className="wb-section !p-4 overflow-hidden">
                <div className="flex flex-wrap items-center gap-2 mb-3">

                    <Button
                        type="primary"
                        className="wb-btn-primary rounded-[8px]"
                        icon={<PlusOutlined />}
                        loading={batchCollecting}
                        onClick={collectSelected}
                    >
                        {t('talent.add_to_mine')}
                    </Button>
                    <Button
                        className="rounded-[8px]"
                        disabled={selectedRowKeys.length === 0}
                        onClick={() => {
                            const selectedKeySet = new Set(selectedRowKeys.map(String));
                            openOutreach(records.filter((row) => selectedKeySet.has(rowKeyOf(row))));
                        }}
                    >
                        {t('talent.batch_outreach')}
                    </Button>
                    <Button
                        className="rounded-[8px]"
                        disabled={selectedRowKeys.length === 0}
                        onClick={() => {
                            const selectedKeySet = new Set(selectedRowKeys.map(String));
                            openCreateInvite(records.filter((row) => selectedKeySet.has(rowKeyOf(row))));
                        }}
                    >
                        {t('talent.batch_invite')}
                    </Button>
                    <Button className="rounded-[8px]" onClick={() => navigate('/creator/creator_manager')}>
                        {t('talent.my_talents')}
                    </Button>
                </div>
                <Table<InfluencerRecord>
                    rowKey={(row) => rowKeyOf(row)}
                    dataSource={records}
                    loading={loading}
                    pagination={{
                        current: pageNum,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                        onChange: (current, size) => {
                            if (size !== pageSize) {
                                setPageNum(1);
                                setPageSize(size);
                                return;
                            }
                            setPageNum(current);
                        },
                    }}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                        getCheckboxProps: (row) => ({ disabled: isBlacklistedTalent(row) }),
                    }}
                    rowClassName={(record) => (isBlacklistedTalent(record) ? 'bg-red-50/80' : '')}
                    columns={columns}
                />
            </section>

            <QuickOutreachModal
                open={outreachOpen}
                presetTalents={outreachPresets}
                onClose={() => setOutreachOpen(false)}
            />
            <CreateTargetInviteModal
                open={createInviteOpen}
                presetTalents={createInviteTalents}
                onClose={() => setCreateInviteOpen(false)}
            />
            <AddBlacklistModal
                open={blacklistTarget != null}
                influencerId={blacklistTarget?.influencerId}
                talentName={blacklistTarget?.talentName}
                onClose={() => setBlacklistTarget(null)}
                onAdded={(influencerId) => {
                    setRecords((prev) =>
                        prev.map((item) =>
                            influencerIdOf(item) === influencerId ? { ...item, isBlacklist: true } : item
                        )
                    );
                }}
            />
        </div>
    );
});
